import React, { useState } from "react";
import { toast } from "sonner";
import { Button, Card, Pill } from "@/components/ui-kit";
import { PRICING } from "@/landingContent";
import { startCheckout, startPortal } from "@/lib/checkout";
import { X, Check, CreditCard } from "lucide-react";

// Plan picker modal for logged-in parents. Monthly/annual toggle; Subscribe
// starts a Stripe checkout tied to the parent's user id. Subscribers also get
// a "Manage billing" link to Stripe's customer portal.
export default function PlanModal({ userId, currentPlan, subscribed, reason, recommended, onClose }) {
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const paidPlans = PRICING.plans.filter((p) => !p.free);

  const subscribe = async (plan) => {
    const lookup = billing === "yearly" ? plan.lookupYearly : plan.lookupMonthly;
    setBusy(lookup);
    try {
      await startCheckout(lookup, userId);
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setBusy(null);
    }
  };

  const manageBilling = async () => {
    setPortalBusy(true);
    try {
      await startPortal();
    } catch {
      toast.error("Couldn't open billing. Please try again.");
      setPortalBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 overflow-y-auto" onClick={onClose} data-testid="plan-modal">
      <Card className="w-full max-w-2xl p-6 ns-rise my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head text-xl font-bold">Choose your plan</h3>
          <button onClick={onClose} data-testid="plan-modal-close" className="text-[var(--ns-subtle)] hover:text-[var(--ns-text)]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[var(--ns-muted)] mb-4">Cancel anytime. Test mode — use card 4242 4242 4242 4242.</p>

        {reason && (
          <div className="mb-4 rounded-[var(--ns-radius)] p-3 text-sm" style={{ background: "#FBEDE6", color: "var(--ns-text)" }} data-testid="plan-reason">
            {reason}
          </div>
        )}

        {subscribed && (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[var(--ns-radius)] border border-[var(--ns-border)] bg-[var(--ns-surface-subtle)] p-3">
            <span className="text-sm text-[var(--ns-text)]">You're subscribed. Update your card, switch plans, or cancel in the billing portal.</span>
            <Button variant="outline" data-testid="manage-billing-btn" disabled={portalBusy} onClick={manageBilling}>
              <CreditCard size={16} /> {portalBusy ? "Opening…" : "Manage billing"}
            </Button>
          </div>
        )}

        <div className="flex rounded-full bg-[var(--ns-surface-subtle)] p-1 w-fit mb-5">
          {[["monthly", "Monthly"], ["yearly", "Annual · save ~17%"]].map(([v, l]) => (
            <button key={v} data-testid={`billing-${v}`} onClick={() => setBilling(v)}
              className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
              style={{ background: billing === v ? "var(--ns-surface)" : "transparent", color: billing === v ? "var(--ns-primary)" : "var(--ns-muted)" }}>
              {l}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {paidPlans.map((p) => {
            const price = billing === "yearly" ? p.yearly : p.monthly;
            const lookup = billing === "yearly" ? p.lookupYearly : p.lookupMonthly;
            const isCurrent = currentPlan === lookup;
            const isRecommended = recommended === p.id;
            return (
              <Card key={p.id} className={`p-5 flex flex-col ${(p.highlight || isRecommended) ? "ring-2 ring-[var(--ns-primary)]" : ""}`} data-testid={`plan-modal-${p.id}`}>
                {isRecommended ? <Pill tone="primary" className="self-start mb-2" data-testid={`recommended-${p.id}`}>Recommended for you</Pill>
                  : p.highlight ? <Pill tone="primary" className="self-start mb-2">Most popular</Pill> : null}
                <h4 className="font-head text-lg font-bold">{p.name}</h4>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-head text-3xl font-bold">${price}</span>
                  <span className="text-sm text-[var(--ns-subtle)]">/{billing === "yearly" ? "yr" : "mo"}</span>
                </div>
                <ul className="mt-3 space-y-1.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[var(--ns-muted)]"><Check size={15} className="text-[var(--ns-primary)] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Button className="mt-4 w-full" data-testid={`subscribe-${p.id}`} disabled={busy === lookup || isCurrent}
                  onClick={() => subscribe(p)}>
                  {isCurrent ? "Current plan" : busy === lookup ? "Redirecting…" : `Choose ${p.name}`}
                </Button>
              </Card>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
